import React, { useState, useEffect } from "react";
import ExaminationSidebar from "../../components/sidebar/ExaminationSidebar";
import ExaminationNavbar from "../../components/navbar/ExaminationNavbar.jsx";
import LineCharts from "../../components/chart/LineChart";
import ExaminationCard from "../../components/card/ExaminationCard.jsx";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import Select from "react-select";
import { serverUrl } from "../../temp.js";
import "./home.scss";

const ExaminationPortal = () => {
  const centerId = localStorage.getItem("center_id");
  const [token] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(
    localStorage.getItem("sidebar") || false
  );
  const [selectedClassId, setSelectedClassId] = useState(() => {
    const stored = localStorage.getItem("class-id");
    return stored ? stored : "7"; // Default to class ID 7 if nothing stored
  });

  // Dropdown states
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);

  // Options states
  const [monthOptions, setMonthOptions] = useState([]);
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [teacherOptions, setTeacherOptions] = useState([]);
  const [studentOptions, setStudentOptions] = useState([]);

  // Chart data state
  const [chartData, setChartData] = useState([]);

  // Card data state
  const [cardData, setCardData] = useState({
    teacher_count: 0,
    student_count: 0,
    class_average: 0,
    course_covered: 0,
    invigilator_attendance: 0,
    teacher_attendance: 0,
    targeted_course_covered: 0,
  });

  // Loading states
  const [loading, setLoading] = useState({
    months: true,
    subjects: true,
    teachers: true,
    students: true,
    chart: true,
    cards: true,
  });

  const redirectToLogin = () => {
    alert("Please Login first then you can access this page...");
    window.location.href = "/";
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
    localStorage.setItem("sidebar", !sidebarOpen);
  };

  const handleClassChange = (newClassId) => {
    setSelectedClassId(newClassId);
    localStorage.setItem("class-id", newClassId);

    // Reset filters when class changes
    setSelectedMonths([]);
    setSelectedSubjects([]);
    setSelectedTeachers([]);
    setSelectedStudents([]);
  };

  // Fetch month options
  useEffect(() => {
    fetch(
      `${serverUrl}/result_dashboard/month_dropdown?centerId=${centerId}&classId=${selectedClassId}&classId=${selectedClassId}`
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.code === "200") {
          setMonthOptions(
            data.data.map((month) => ({
              value: month.month,
              label: month.month_name,
            }))
          );
        }
        setLoading((prev) => ({ ...prev, months: false }));
      })
      .catch((error) => {
        console.error("Error fetching months:", error);
        setLoading((prev) => ({ ...prev, months: false }));
      });
  }, [selectedClassId]);

  // Fetch subject options when class changes
  useEffect(() => {
    if (!selectedClassId) return;

    setLoading((prev) => ({ ...prev, subjects: true }));
    fetch(
      `${serverUrl}/result_dashboard/subject_dropdown?centerId=${centerId}&classId=${selectedClassId}`
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.code === "200") {
          setSubjectOptions(
            data.data.map((subject) => ({
              value: subject.subject_id,
              label: subject.subject_name,
            }))
          );
        }
        setLoading((prev) => ({ ...prev, subjects: false }));
      })
      .catch((error) => {
        console.error("Error fetching subjects:", error);
        setLoading((prev) => ({ ...prev, subjects: false }));
      });
  }, [selectedClassId]);

  // Fetch teacher options when subjects change
  useEffect(() => {
    if (!selectedClassId) {
      setTeacherOptions([]);
      setSelectedTeachers([]);
      return;
    }

    setLoading((prev) => ({ ...prev, teachers: true }));
    const subjectIds = selectedSubjects.map((sub) => sub.value);

    fetch(
      `${serverUrl}/result_dashboard/teacher_dropdown?centerId=${centerId}&classId=${selectedClassId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ subjectId: subjectIds }),
      }
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.code === "200") {
          setTeacherOptions(
            data.data.map((teacher) => ({
              value: teacher.id,
              label: teacher.teacher_name,
            }))
          );
        }
        setLoading((prev) => ({ ...prev, teachers: false }));
      })
      .catch((error) => {
        console.error("Error fetching teachers:", error);
        setLoading((prev) => ({ ...prev, teachers: false }));
      });
  }, [selectedClassId, selectedSubjects]);

  // Fetch student options when class changes
  useEffect(() => {
    if (!selectedClassId) return;

    setLoading((prev) => ({ ...prev, students: true }));
    fetch(
      `${serverUrl}/result_dashboard/student_dropdown?centerId=${centerId}&classId=${selectedClassId}`
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.code === "200") {
          setStudentOptions(
            data.data.map((student) => ({
              value: student.id,
              label: student.name,
            }))
          );
        }
        setLoading((prev) => ({ ...prev, students: false }));
      })
      .catch((error) => {
        console.error("Error fetching students:", error);
        setLoading((prev) => ({ ...prev, students: false }));
      });
  }, [selectedClassId]);

  // Fetch chart data when any filter changes
  useEffect(() => {
    if (!selectedClassId) return;

    setLoading((prev) => ({ ...prev, chart: true }));
    const monthIds = selectedMonths.map((m) => m.value);
    const subjectIds = selectedSubjects.map((s) => s.value);

    fetch(
      `${serverUrl}/result_dashboard/chart/class-average?centerId=${centerId}&classId=${selectedClassId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          monthids: monthIds,
          subjectids: subjectIds,
          // Removed teacherids and studentids from the request
        }),
      }
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.code === "200") {
          setChartData(data.data);
        }
        setLoading((prev) => ({ ...prev, chart: false }));
      })
      .catch((error) => {
        console.error("Error fetching chart data:", error);
        setLoading((prev) => ({ ...prev, chart: false }));
      });
  }, [selectedClassId, selectedMonths, selectedSubjects]);

  // Fetch card data when any filter changes
  useEffect(() => {
    if (!selectedClassId) return;

    setLoading((prev) => ({ ...prev, cards: true }));
    const monthIds = selectedMonths.map((m) => m.value);
    const subjectIds = selectedSubjects.map((s) => s.value);
    const teacherIds = selectedTeachers.map((t) => t.value);
    const studentIds = selectedStudents.map((s) => s.value);

    fetch(
      `${serverUrl}/result_dashboard/home_card_data?centerId=${centerId}&classId=${selectedClassId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          months: monthIds,
          subjects: subjectIds,
          teachers: teacherIds,
          students: studentIds,
        }),
      }
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.code === "200") {
          setCardData(data.data);
        }
        setLoading((prev) => ({ ...prev, cards: false }));
      })
      .catch((error) => {
        console.error("Error fetching card data:", error);
        setLoading((prev) => ({ ...prev, cards: false }));
      });
  }, [
    selectedClassId,
    selectedMonths,
    selectedSubjects,
    selectedTeachers,
    selectedStudents,
  ]);

  // Helper to format applied filters
  const formatAppliedFilters = (filters) => {
    return filters
      .filter((filter) => filter.value.length > 0)
      .map((filter) => ({
        label: filter.label,
        value: filter.value.map((v) => v.label).join(", "),
      }));
  };

  // Get applied filters for cards
  const cardFilters = formatAppliedFilters([
    { label: "Month", value: selectedMonths },
    { label: "Subject", value: selectedSubjects },
    { label: "Teacher", value: selectedTeachers },
    { label: "Student", value: selectedStudents },
  ]);

  // Get applied filters for chart (only month and subject)
  const chartFilters = formatAppliedFilters([
    { label: "Month", value: selectedMonths },
    { label: "Subject", value: selectedSubjects },
  ]);

  return (
    <>
      {!token && redirectToLogin()}
      {token && (
        <div className="home">
          {sidebarOpen && (
            <ExaminationSidebar isOpen={sidebarOpen} toggle={toggleSidebar} />
          )}
          <div className="homeContainer">
            <ExaminationNavbar
              isOpen={sidebarOpen}
              toggle={toggleSidebar}
              selectedClassId={selectedClassId}
              onClassChange={handleClassChange}
            />

            {/* Add Welcome Heading Here */}
            <div className="welcome-heading">
              <h1>Welcome to Result Dashboard</h1>
            </div>

            <div className="widgets">
              <div className="dropdown-container">
                <label htmlFor="dropdown1">Month</label>
                <Select
                  id="dropdown1"
                  isMulti
                  options={monthOptions}
                  value={selectedMonths}
                  onChange={setSelectedMonths}
                  isLoading={loading.months}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>

              <div className="dropdown-container">
                <label htmlFor="dropdown2">Subject</label>
                <Select
                  id="dropdown2"
                  isMulti
                  options={subjectOptions}
                  value={selectedSubjects}
                  onChange={setSelectedSubjects}
                  isLoading={loading.subjects}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>

              <div className="dropdown-container">
                <label htmlFor="dropdown3">Teacher</label>
                <Select
                  id="dropdown3"
                  isMulti
                  options={teacherOptions}
                  value={selectedTeachers}
                  onChange={setSelectedTeachers}
                  isLoading={loading.teachers}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>

              <div className="dropdown-container">
                <label htmlFor="dropdown4">Student</label>
                <Select
                  id="dropdown4"
                  isMulti
                  options={studentOptions}
                  value={selectedStudents}
                  onChange={setSelectedStudents}
                  isLoading={loading.students}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>
            </div>

            <div className="widgets">
              <ExaminationCard
                type={"Total Teacher"}
                value={cardData.teacher_count}
                icon={<PersonOutlinedIcon />}
                loading={loading.cards}
                appliedFilters={cardFilters}
              />
              <ExaminationCard
                type={"Total Student"}
                value={cardData.student_count}
                icon={<PersonOutlinedIcon />}
                loading={loading.cards}
                appliedFilters={cardFilters}
              />
              <ExaminationCard
                type={"Class Average"}
                value={`${parseFloat(cardData.class_average || 0).toFixed(2)}%`}
                icon={<PersonOutlinedIcon />}
                loading={loading.cards}
                appliedFilters={cardFilters}
              />
              {/* <ExaminationCard type={"Present (TBD)"} value={"6 %"} icon={<PersonOutlinedIcon />} loading={loading.cards}
                                appliedFilters={cardFilters} /> */}
            </div>

            <div className="widgets">
              <ExaminationCard
                type={"Teacher Attendance"}
                value={`${parseFloat(cardData.teacher_attendance || 0).toFixed(
                  2
                )}%`}
                icon={<PersonOutlinedIcon />}
                loading={loading.cards}
                appliedFilters={cardFilters}
              />
              {/* <ExaminationCard
                                type={"Invigilator Attendence"}
                                value={`${parseFloat(cardData.invigilator_attendance || 0).toFixed(2)}%`}
                                icon={<PersonOutlinedIcon />}
                                loading={loading.cards}
                                appliedFilters={cardFilters}
                            /> */}

              <ExaminationCard
                type={"Targeted Course"}
                value={`${parseFloat(
                  cardData.targeted_course_covered || 0
                ).toFixed(2)}%`}
                icon={<PersonOutlinedIcon />}
                loading={loading.cards}
                appliedFilters={cardFilters}
              />
              <ExaminationCard
                type={"Course Covered"}
                value={`${parseFloat(cardData.course_covered || 0).toFixed(
                  2
                )}%`}
                icon={<PersonOutlinedIcon />}
                loading={loading.cards}
                appliedFilters={cardFilters}
              />
            </div>

            <div className="charts">
              <LineCharts
                title="Class Average"
                data={chartData.map((item) => item.average_marks)}
                labels={chartData.map((item) => item.month_name)}
                loading={loading.chart}
                appliedFilters={chartFilters}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ExaminationPortal;
